import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useSWR from 'swr';
import { Maximize2 } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Combobox } from '@/components/ui/combobox';
import { ImageLightbox } from '@/components/ui/image-lightbox';
import { useFrameTime } from '@/components/ui/hover-time';
import { useT } from '@/contexts/I18nContext';
import { getLayoutDetail, whDomain } from '@/api/analytics';
import { ShelfTimeseriesChart } from '@/pages/Management/AnalyticsLayoutDetail';
import {
  DayPhotoStrip,
  nearestPhotoIndex,
  buildRackStrip,
  type DayStripPhoto,
} from './DayPhotoStrip';
import { DayChartLightbox } from './DayChartLightbox';
import { RackMiniGrid } from './RackMiniGrid';
import { useChartTouchTooltipGuard } from './chartTouchTooltipGuard';
import { cn } from '@/lib/utils';

const CARD_TITLE = 'text-xs font-semibold uppercase tracking-wider text-muted-foreground';
// ShelfTimeseriesChart's plot-area insets (YAxis width 44 + right margin 8) —
// the photo strip and the lightbox marquee both align to these.
const PLOT_INSET = { left: 44, right: 8, bottom: 52 };
// The card strip now widens + scrolls horizontally instead of cropping
const CHART_H = 200;
// «Все» is a special FIRST item in the rack dropdown (mirrors «Все филиалы» in
// the branch selector + the analytics rack picker), not a separate toggle.
const ALL_RACKS_VALUE = 'all';

/** A request from elsewhere in the report (a "Посмотреть" button) to open this
 *  card's fullscreen chart zoomed to a specific window, optionally for a named
 *  rack. `nonce` makes repeat clicks of the same button re-fire. */
export interface RacksFocusRequest {
  domain: [number, number];
  rackName?: string;
  /** Exact rack (BE deep links carry `rack_id=` since 2026-09-08); wins over
   *  the name match, which is ambiguous across same-named racks. */
  rackId?: number;
  /** Unpadded problem window — shaded on the fullscreen chart. */
  window?: [number, number];
  /** Select the rack ON THE CARD (the compact consult panel) instead of
   *  opening the fullscreen lightbox — the report constructor's category
   *  link (Alexey 2026-09-07: "чтобы прямо справа открылся стеллаж выпечка в
   *  панели", not a new tab). */
  inPanel?: boolean;
  nonce: number;
}

function clampDomain(
  zoom: [number, number],
  bounds: [number, number] | undefined,
): [number, number] {
  if (!bounds) return zoom;
  return [Math.max(zoom[0], bounds[0]), Math.min(zoom[1], bounds[1])];
}
// Pre-load PLACEHOLDER slot width (~1.4:1 at 130px height). Once the rack's
// first photo loads the strip re-sizes every slot to the crop's real aspect
// (823), so this only reserves the row before images arrive; it is no longer a
// hardcoded final width. Was 56 — unreadably small; a wider placeholder keeps
// the pre-load row close to the measured one.
const RACK_SLOT_W = 180;


/**
 * Racks card for the "День" report — the shelf area chart for the day at
 * 15-minute granularity, with a rack-picker DROPDOWN. Images sit ABOVE the
 * chart, time-aligned along X on desktop (grouped on mobile). The chart X
 * domain follows working hours; clicking a chart point opens the nearest photo.
 */
export function DayRacksCard({
  zoneId,
  day,
  focusRequest,
  onFocusHandled,
  compact,
}: {
  zoneId: string;
  day: string;
  focusRequest?: RacksFocusRequest | null;
  onFocusHandled?: () => void;
  /** Shrinks header padding + chart height — used by the validation page's
   *  consult-chart column so it (plus the queue card + text editor) fits one
   *  screen without page scroll. Doesn't affect the public /usage/report page. */
  compact?: boolean;
}) {
  const { t } = useT();
  const ft = useFrameTime();
  const [rackId, setRackId] = useState<number | null>(null);
  const [openIdx, setOpenIdx] = useState<number | null>(null);
  const [expanded, setExpanded] = useState(false);
  // Rack shown in the fullscreen lightbox — kept SEPARATE from the on-page
  // chart's `rackId` so a "Посмотреть: <rack>" button can zoom the lightbox to
  // a problem rack WITHOUT hijacking the rack the operator picked on the card.
  const [lightboxRackId, setLightboxRackId] = useState<number | null>(null);
  // Preset zoom window for the fullscreen chart when opened from a "Посмотреть"
  // button; null → the lightbox opens at the full day.
  const [presetZoom, setPresetZoom] = useState<[number, number] | null>(null);
  // The problem window itself (unpadded) — drawn as a shaded band on the
  // fullscreen chart so it stays visible after a zoom-out to the whole day.
  const [presetBand, setPresetBand] = useState<[number, number] | null>(null);
  // "Все стеллажи" mode — a grid of per-rack mini charts instead of the single
  // rack's strip+chart. Tracked separately for the page card and the lightbox.
  const [showAll, setShowAll] = useState(false);
  const [lbShowAll, setLbShowAll] = useState(false);
  // FIXED over-request so the SWR key never changes as the container width
  // settles on load — a width-derived count refetched a different picked set and
  // the photos flickered in/out (Alexey 2026-07-21). The strip renders as many
  // as fit; the fetch (and thus the photo set) loads once and stays put.
  const photoCount = 40;

  // First request without rack_id — backend picks the priority-first default.
  // 15-minute points + dip-mode photos for the day card.
  const { data, isLoading, isValidating } = useSWR(
    zoneId ? ['report:day-racks', zoneId, rackId, day, photoCount] : null,
    () =>
      getLayoutDetail(zoneId, day, day, rackId ?? undefined, '15min', {
        // Even spread fills the width; the FE then orange-marks the biggest dips.
        photoMode: 'even',
        photoCount,
      }).then((r) => r.data),
    // Keep the previous rack's chart on screen while a rack switch reloads, so
    // the fullscreen lightbox never blanks out mid-open when a "Посмотреть"
    // button both selects a rack and zooms.
    { keepPreviousData: true, revalidateOnFocus: false },
  );

  // The lightbox can show a DIFFERENT rack than the on-page card (a problem-zone
  // button targets a specific rack). Fetch that rack's data independently, only
  // while the lightbox is open. When it's the same rack as the card the SWR key
  // matches — but by the time the lightbox opens the card's own fetch is
  // usually well past SWR's dedupingInterval, so a plain useSWR here still
  // fires a real background revalidation on mount even though the data is
  // already fresh (Alexey 2026-09-07: saw the exact same layout-detail URL
  // requested twice at once, card felt like it "loads forever" opening the
  // lightbox). revalidateIfStale:false makes it just reuse the card's cached
  // response instead — genuinely no extra request when it's the same rack.
  const { data: lightboxData, isValidating: lightboxValidating } = useSWR(
    expanded && zoneId && lightboxRackId != null
      ? ['report:day-racks', zoneId, lightboxRackId, day, photoCount]
      : null,
    () =>
      getLayoutDetail(zoneId, day, day, lightboxRackId ?? undefined, '15min', {
        photoMode: 'even',
        photoCount,
      }).then((r) => r.data),
    { keepPreviousData: true, revalidateOnFocus: false, revalidateIfStale: false },
  );

  // A "Посмотреть: <rack>" button elsewhere in the report asked to open this
  // card's fullscreen chart zoomed to a problem window (and, when named, for
  // that specific rack). Fire once per click (keyed by nonce). The problem rack
  // is routed to the LIGHTBOX only — the on-page chart's `rackId` is left alone.
  useEffect(() => {
    if (!focusRequest) return;
    // A named/id'd request that arrives before the rack list (a Telegram deep
    // link on page load) must WAIT for the list — resolving it against an
    // empty list silently opened the default rack instead (Alexey
    // 2026-09-08: «диплинки из отчётов не включают rack type»). The parent
    // keeps the request until onFocusHandled, so re-running on racks arrival
    // is safe.
    const wantsRack = focusRequest.rackId != null || !!focusRequest.rackName;
    if (wantsRack && !data?.racks?.length) return;
    let targetId = rackId ?? data?.selected_rack?.id ?? data?.racks?.[0]?.id ?? null;
    if (focusRequest.rackId != null && data?.racks?.some((r) => r.id === focusRequest.rackId)) {
      targetId = focusRequest.rackId;
    } else if (focusRequest.rackName && data?.racks?.length) {
      const match = data.racks.find((r) => r.name === focusRequest.rackName);
      if (match) targetId = match.id;
    }
    if (focusRequest.inPanel) {
      if (targetId != null) setRackId(targetId);
      setShowAll(false);
      onFocusHandled?.();
      return;
    }
    setLightboxRackId(targetId);
    setPresetZoom(focusRequest.domain);
    setPresetBand(focusRequest.window ?? null);
    setLbShowAll(false);
    setExpanded(true);
    onFocusHandled?.();
    // Keyed on the click nonce + rack-list arrival (exhaustive-deps rule is
    // not enabled in this project; see reference_fe_test_harness).
  }, [focusRequest?.nonce, data?.racks?.length]);

  const racks = data?.racks ?? [];
  const selectedId = rackId ?? data?.selected_rack?.id ?? racks[0]?.id ?? null;
  const photos = data?.rack_photos ?? [];
  const wh = data?.working_hours ?? null;

  const domain = useMemo<[number, number] | undefined>(() => {
    if (wh) {
      // Padded VISUAL domain (chart X + strip axis); timing reads wh.start (888).
      const d = whDomain(wh);
      const s = new Date(d.start).getTime();
      const e = new Date(d.end).getTime();
      if (Number.isFinite(s) && Number.isFinite(e) && e > s) return [s, e];
    }
    return undefined;
  }, [wh]);

  const stripPhotos: DayStripPhoto[] = useMemo(() => buildRackStrip(photos), [photos]);
  // Lightbox gallery = EXACTLY the photos the strip renders (reported via
  // onVisiblePhotos), so swiping never pages past what's on screen.
  const [lbPhotos, setLbPhotos] = useState<DayStripPhoto[]>([]);
  const srcs = lbPhotos.map((p) => p.image_url!).filter(Boolean);
  const captions = lbPhotos.map((p) => {
    const tlabel = ft(p.at);
    return `${p.metric ? `${p.metric} · ` : ''}${tlabel}`.trim();
  });

  const openNearest = (momentIso: string) => {
    // Index into the RENDERED set (what the lightbox shows), not all photos.
    const idx = nearestPhotoIndex(lbPhotos, momentIso);
    if (idx >= 0) setOpenIdx(idx);
  };

  // The card's own expand button opens the lightbox on the CURRENTLY selected
  // rack, at the full day (no preset zoom), carrying over the all-racks mode.
  const openOwnLightbox = () => {
    setLightboxRackId(selectedId);
    setPresetZoom(null);
    setLbShowAll(showAll);
    setExpanded(true);
  };

  // NOT gated on isLoading: while a rack switch revalidates, the previous
  // rack's data stays (keepPreviousData) — hiding the button made the header
  // jump on every switch.
  const canExpand = !!data && !!domain && data.shelf_timeseries.points.length > 0;

  // A drag on the in-card chart is a failed marquee attempt, not a click —
  // suppress the click-to-open-photo handler after real drags.
  const clickGuard = useRef(false);
  const guardStart = useRef<{ x: number; y: number } | null>(null);
  const touchTooltipGuard = useChartTouchTooltipGuard();
  const renderChart = ({ domain: dom, height }: { domain: [number, number]; height: number }) => (
    <div
      onPointerDownCapture={(e) => {
        guardStart.current = { x: e.clientX, y: e.clientY };
        clickGuard.current = false;
      }}
      onPointerUpCapture={(e) => {
        const s = guardStart.current;
        clickGuard.current = !!s && Math.hypot(e.clientX - s.x, e.clientY - s.y) > 8;
      }}
      {...touchTooltipGuard}
    >
      <ShelfTimeseriesChart
        data={data!.shelf_timeseries}
        granularity="15min"
        label=""
        bare
        height={height}
        domain={dom}
        onPointClick={(iso) => {
          if (!clickGuard.current) openNearest(iso);
        }}
      />
    </div>
  );

  // ── Lightbox (fullscreen) view — driven by `lightboxData` so it can show a
  //    different rack than the on-page chart. Falls back to the card's data
  //    until the independent fetch resolves. ──
  const lbData = lightboxData ?? data;
  // The lightbox rack was just switched and its payload hasn't landed yet —
  // `lbData` still describes the PREVIOUS rack (keepPreviousData). Show the
  // loader rather than that stale strip/chart (Alexey 2026-09-08: «нет
  // лоадера нигде, стоит старая картинка»).
  const lbLoading =
    expanded &&
    lightboxRackId != null &&
    !lbShowAll &&
    ((lbData?.selected_rack?.id ?? null) !== lightboxRackId || lightboxValidating);
  const lbStrip: DayStripPhoto[] = buildRackStrip(lbData?.rack_photos ?? []);
  const lbWh = lbData?.working_hours ?? null;
  const lbDomain: [number, number] | undefined = (() => {
    if (lbWh) {
      const d = whDomain(lbWh);
      const s = new Date(d.start).getTime();
      const e = new Date(d.end).getTime();
      if (Number.isFinite(s) && Number.isFinite(e) && e > s) return [s, e];
    }
    return domain;
  })();
  const lbRenderChart = ({ domain: dom, height }: { domain: [number, number]; height: number }) =>
    lbLoading ? (
      <div className="animate-pulse rounded-xl bg-muted" style={{ height }} />
    ) : lbData ? (
      <ShelfTimeseriesChart
        data={lbData.shelf_timeseries}
        granularity="15min"
        label=""
        bare
        height={height}
        domain={dom}
        highlight={presetBand ?? undefined}
      />
    ) : null;

  // Load denser frames for the lightbox's window (full day or a zoom), sized
  // to the lightbox's own width. Keyed on the lightbox rack, not the card's pick.
  const loadWindowRackPhotos = useCallback(
    async (dom: [number, number], count: number): Promise<DayStripPhoto[]> => {
      const r = await getLayoutDetail(zoneId, day, day, lightboxRackId ?? undefined, '15min', {
        photoMode: 'even',
        photoCount: count,
        fromTs: new Date(dom[0]).toISOString(),
        toTs: new Date(dom[1]).toISOString(),
      });
      return buildRackStrip(r.data.rack_photos ?? []);
    },
    [zoneId, day, lightboxRackId],
  );

  // Not configured → render nothing (the card only appears for zones with
  // racks). AFTER every hook — an early return above a hook is the classic
  // React #300 (hook-count mismatch between renders).
  if (!isLoading && data && !data.racks.length) return null;

  const chartH = compact ? 130 : CHART_H;

  return (
    <Card>
      <CardHeader
        className={cn(
          'flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between',
          compact ? 'pt-2 pb-1' : 'pt-4 pb-2',
        )}
      >
        <div className="flex items-center justify-between gap-2 sm:justify-start">
          <CardTitle className={CARD_TITLE}>{t('report.day.racksCardTitle')}</CardTitle>
          {canExpand && (
            <button
              type="button"
              onClick={openOwnLightbox}
              className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground sm:hidden"
              aria-label={t('report.day.expand')}
            >
              <Maximize2 className="size-4" />
            </button>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Rack picker: ONE dropdown with «Все» as the special first item
              (mirrors «Все филиалы» + the analytics rack picker), not a separate
              toggle. Only the rack NAME — the type is almost always identical to
              the name ("Готовая еда - Готовая еда"), so it's just noise here. */}
          {racks.length > 1 && (
            <div className="min-w-0 flex-1 sm:min-w-44 sm:flex-none">
              <Combobox
                value={showAll ? ALL_RACKS_VALUE : selectedId != null ? String(selectedId) : ''}
                onChange={(v) => {
                  if (v === ALL_RACKS_VALUE) {
                    setShowAll(true);
                  } else {
                    setRackId(Number(v));
                    setShowAll(false);
                  }
                }}
                options={[
                  { value: ALL_RACKS_VALUE, label: t('report.day.allRacks') },
                  ...racks.map((r) => ({ value: String(r.id), label: r.name })),
                ]}
                className="h-8 rounded-full text-xs shadow-none"
                id="day-racks-picker"
              />
            </div>
          )}
          {canExpand && (
            <button
              type="button"
              onClick={openOwnLightbox}
              className="hidden shrink-0 rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground sm:inline-flex"
              aria-label={t('report.day.expand')}
            >
              <Maximize2 className="size-4" />
            </button>
          )}
        </div>
      </CardHeader>
      <CardContent className={compact ? 'space-y-1.5' : 'space-y-3'}>
        {/* Alexey (2026-09-07): a rack switch used to keep the OLD rack's
            chart on screen with just a small spinner next to the picker as
            the only cue ("не реагирует лоадером... выглядит зависшим" was
            the ORIGINAL complaint that spinner fixed) — but the stale
            content itself is pointless to hold onto and reads as confusing/
            slow ("незачем держать старый контент"). The loader now REPLACES
            the content area for any in-flight fetch (isValidating), not
            just the very first load. */}
        {(isLoading || isValidating) && <div className="h-72 animate-pulse rounded-xl bg-muted" />}

        {data && !isValidating && showAll && (
          <RackMiniGrid
            zoneId={zoneId}
            day={day}
            racks={racks}
            onPick={(id) => {
              setRackId(id);
              setShowAll(false);
            }}
          />
        )}

        {data && !isValidating && !showAll && (
          <>
            {/* Images ABOVE the chart. */}
            <div>
              {stripPhotos.length > 0 && (
                <DayPhotoStrip
                  photos={stripPhotos}
                  workStart={wh ? whDomain(wh).start : undefined}
                  workEnd={wh ? whDomain(wh).end : undefined}
                  onOpen={setOpenIdx}
                  onVisiblePhotos={setLbPhotos}
                  slotW={RACK_SLOT_W}
                  // Placeholder width; strip re-sizes slots to the rack's aspect
                  // on first load (823). imgH is the fixed lane HEIGHT.
                  imgH={130}
                  // Wide «Готовая еда» crops must still show ≥6 — count wins
                  // over natural aspect, slots shrink to fit (847/848).
                  minCount={6}
                  // Rack dips cluster in the opening stocking ramp — pick photos
                  // to COVER the whole day (even spread + surface midday dips),
                  // else the afternoon shows nothing (Alexey zone 19 2026-07-22).
                  coverageFirst
                  // Mobile scrolls horizontally, so crops can be bigger there
                  // (owner: card photos too small on phones).
                  mobileScale={1.6}
                  axisInset={{ left: PLOT_INSET.left, right: PLOT_INSET.right }}
                />
              )}
            </div>

            {domain && renderChart({ domain, height: chartH })}
          </>
        )}

        {openIdx != null && (
          <ImageLightbox
            srcs={srcs}
            index={openIdx}
            alt=""
            captions={captions}
            onClose={() => setOpenIdx(null)}
            onNavigate={setOpenIdx}
          />
        )}
      </CardContent>

      {expanded && lbDomain && lbData && (
        <DayChartLightbox
          // Just «Выкладка за день» — the rack dropdown in the header already
          // names the rack, so «· Готовая еда» in the title was a duplicate.
          title={t('report.day.racksCardTitle')}
          photos={lbLoading ? [] : lbStrip}
          contentKey={lightboxRackId}
          contentLoading={lbLoading}
          captionOf={(p) => {
            const tlabel = ft(p.at);
            return `${p.metric ? `${p.metric} · ` : ''}${tlabel}`.trim();
          }}
          fullDomain={lbDomain}
          initialZoom={presetZoom ? clampDomain(presetZoom, lbDomain) : null}
          slotW={RACK_SLOT_W}
          cardImgH={130}
          plotInset={PLOT_INSET}
          renderChart={lbRenderChart}
          loadWindowPhotos={loadWindowRackPhotos}
          // Fullscreen = MORE photos (2 staggered rows), same rule as Столы.
          stripLanes={2}
          // Cover the whole day (see the card strip above) — no afternoon gap.
          coverageFirst
          allRacks={
            racks.length > 1
              ? {
                  active: lbShowAll,
                  onToggle: () => setLbShowAll((v) => !v),
                  label: t('report.day.allRacks'),
                  // «Все» as the first dropdown item (mirrors the card picker),
                  // and switching a rack in fullscreen without closing.
                  picker: {
                    value: lbShowAll
                      ? ALL_RACKS_VALUE
                      : lightboxRackId != null
                        ? String(lightboxRackId)
                        : selectedId != null
                          ? String(selectedId)
                          : '',
                    options: [
                      { value: ALL_RACKS_VALUE, label: t('report.day.allRacks') },
                      ...racks.map((r) => ({ value: String(r.id), label: r.name })),
                    ],
                    onChange: (v) => {
                      if (v === ALL_RACKS_VALUE) {
                        setLbShowAll(true);
                      } else {
                        setLightboxRackId(Number(v));
                        setPresetZoom(null);
                        setLbShowAll(false);
                      }
                    },
                  },
                  content: (
                    <RackMiniGrid
                      zoneId={zoneId}
                      day={day}
                      racks={racks}
                      chartHeight={200}
                      className="xl:grid-cols-3"
                      onPick={(id) => {
                        setLightboxRackId(id);
                        setPresetZoom(null);
                        setLbShowAll(false);
                      }}
                    />
                  ),
                }
              : undefined
          }
          onClose={() => {
            setExpanded(false);
            setPresetZoom(null);
          }}
        />
      )}
    </Card>
  );
}
